import { INIT_SIMPLIFIED_BANKROLL } from "../constants";
import { adjustedPlatformTotal } from "./simplifiedBankroll";
import { getPlatformCurrency, normalizeCurrency } from "./currency";
import { getLocalDateString } from "./dateUtils";
import { normalizeSimplifiedBankroll } from "./dataModel";
import { supabase } from "./supabaseClient";
import { fromCents, toCents } from "./supabaseMapping";
import {
  isMissingAliasError,
  isMissingCurrencyColumnError,
  isMissingDepositedCentsError,
  isMissingSnapshotBaseColumnError,
  isMissingWithdrawnCentsError,
} from "./supabaseSchemaErrors";

export const fetchSimplifiedSettings = (userId) =>
  supabase
    .from("simplified_settings")
    .select("base_bankroll_cents")
    .eq("user_id", userId)
    .maybeSingle();

export const fetchSimplifiedSnapshots = async (userId) => {
  const withBase = await supabase
    .from("simplified_bankroll_snapshots")
    .select("snapshot_id,snapshot_date,total_balance_cents,base_bankroll_cents")
    .eq("user_id", userId)
    .order("snapshot_date", { ascending: true });

  if (!isMissingSnapshotBaseColumnError(withBase.error)) {
    return withBase;
  }

  return supabase
    .from("simplified_bankroll_snapshots")
    .select("snapshot_id,snapshot_date,total_balance_cents")
    .eq("user_id", userId)
    .order("snapshot_date", { ascending: true });
};

export const fetchSimplifiedSnapshotPlatforms = async (snapshotIds = []) => {
  if (!snapshotIds.length) return Promise.resolve({ data: [], error: null });

  const withCurrencyAndAlias = await supabase
    .from("simplified_snapshot_platforms")
    .select(
      "snapshot_id,platform_id,balance_cents,balance_currency,simplified_platforms(name,alias)"
    )
    .in("snapshot_id", snapshotIds);

  if (
    !isMissingAliasError(withCurrencyAndAlias.error) &&
    !isMissingCurrencyColumnError(withCurrencyAndAlias.error)
  ) {
    return withCurrencyAndAlias;
  }

  const selectColumns = isMissingCurrencyColumnError(withCurrencyAndAlias.error)
    ? "snapshot_id,platform_id,balance_cents"
    : "snapshot_id,platform_id,balance_cents,balance_currency";
  const platformColumns = isMissingAliasError(withCurrencyAndAlias.error)
    ? "simplified_platforms(name)"
    : "simplified_platforms(name,alias)";
  const fallback = await supabase
    .from("simplified_snapshot_platforms")
    .select(`${selectColumns},${platformColumns}`)
    .in("snapshot_id", snapshotIds);

  if (fallback.error || !isMissingAliasError(withCurrencyAndAlias.error)) {
    return fallback;
  }

  return {
    ...fallback,
    data: (fallback.data || []).map((row) => ({
      ...row,
      simplified_platforms: Array.isArray(row.simplified_platforms)
        ? row.simplified_platforms.map((platform) => ({ ...platform, alias: "" }))
        : { ...(row.simplified_platforms || {}), alias: "" },
    })),
  };
};

export const fetchSimplifiedPlatforms = async (userId) => {
  const withLedger = await supabase
    .from("simplified_platforms")
    .select("platform_id,name,alias,balance_cents,balance_currency,withdrawn_cents,withdrawn_currency,deposited_cents,deposited_currency,is_active")
    .eq("user_id", userId)
    .eq("is_active", true)
    .order("name", { ascending: true });

  if (
    !isMissingWithdrawnCentsError(withLedger.error) &&
    !isMissingDepositedCentsError(withLedger.error) &&
    !isMissingAliasError(withLedger.error) &&
    !isMissingCurrencyColumnError(withLedger.error)
  ) {
    return withLedger;
  }

  if (isMissingCurrencyColumnError(withLedger.error)) {
    const withoutCurrency = await supabase
      .from("simplified_platforms")
      .select("platform_id,name,alias,balance_cents,withdrawn_cents,deposited_cents,is_active")
      .eq("user_id", userId)
      .eq("is_active", true)
      .order("name", { ascending: true });

    if (
      !isMissingWithdrawnCentsError(withoutCurrency.error) &&
      !isMissingDepositedCentsError(withoutCurrency.error) &&
      !isMissingAliasError(withoutCurrency.error)
    ) {
      return withoutCurrency;
    }
  }

  if (isMissingAliasError(withLedger.error)) {
    const withoutAlias = await supabase
      .from("simplified_platforms")
      .select("platform_id,name,balance_cents,withdrawn_cents,deposited_cents,is_active")
      .eq("user_id", userId)
      .eq("is_active", true)
      .order("name", { ascending: true });

    if (
      !isMissingWithdrawnCentsError(withoutAlias.error) &&
      !isMissingDepositedCentsError(withoutAlias.error)
    ) {
      return {
        ...withoutAlias,
        data: (withoutAlias.data || []).map((platform) => ({
          ...platform,
          alias: "",
        })),
      };
    }
  }

  if (isMissingDepositedCentsError(withLedger.error)) {
    const withWithdrawals = await supabase
      .from("simplified_platforms")
      .select("platform_id,name,balance_cents,withdrawn_cents,is_active")
      .eq("user_id", userId)
      .eq("is_active", true)
      .order("name", { ascending: true });

    if (!isMissingWithdrawnCentsError(withWithdrawals.error)) {
      return {
        ...withWithdrawals,
        data: (withWithdrawals.data || []).map((platform) => ({
          ...platform,
          alias: "",
          deposited_cents: 0,
        })),
      };
    }
  }

  const fallback = await supabase
    .from("simplified_platforms")
    .select("platform_id,name,balance_cents,is_active")
    .eq("user_id", userId)
    .eq("is_active", true)
    .order("name", { ascending: true });

  return {
    ...fallback,
    data: (fallback.data || []).map((platform) => ({
      ...platform,
      alias: "",
      withdrawn_cents: 0,
      deposited_cents: 0,
    })),
  };
};

export const mapSimplifiedBankrollFromDb = ({
  settings,
  platforms,
  snapshots,
  snapshotPlatforms,
}) => {
  const platformRows = platforms || [];
  const snapshotPlatformRows = snapshotPlatforms || [];
  const platformsByName = Object.fromEntries(
    platformRows.map((platform) => [
      platform.name,
      fromCents(platform.balance_cents),
    ])
  );
  const withdrawals = Object.fromEntries(
    platformRows.map((platform) => [
      platform.name,
      fromCents(platform.withdrawn_cents),
    ])
  );
  const deposits = Object.fromEntries(
    platformRows.map((platform) => [
      platform.name,
      fromCents(platform.deposited_cents),
    ])
  );
  const accounts = platformRows.map((platform) => ({
    id: platform.platform_id,
    name: platform.name,
    alias: platform.alias || "",
    balance: fromCents(platform.balance_cents),
    balanceCurrency: normalizeCurrency(
      platform.balance_currency,
      getPlatformCurrency(platform.name)
    ),
    withdrawn: fromCents(platform.withdrawn_cents),
    withdrawnCurrency: normalizeCurrency(
      platform.withdrawn_currency,
      getPlatformCurrency(platform.name)
    ),
    deposited: fromCents(platform.deposited_cents),
    depositedCurrency: normalizeCurrency(
      platform.deposited_currency,
      getPlatformCurrency(platform.name)
    ),
  }));
  const platformMetaById = new Map(
    platformRows.map((platform) => [platform.platform_id, platform])
  );
  const snapshotAccountsBySnapshotId = snapshotPlatformRows.reduce((acc, row) => {
    const embeddedPlatform = Array.isArray(row.simplified_platforms)
      ? row.simplified_platforms[0]
      : row.simplified_platforms;
    const platform = embeddedPlatform || platformMetaById.get(row.platform_id);
    const name = platform?.name;

    if (!name) return acc;

    const accountsForSnapshot = acc.get(row.snapshot_id) || [];
    accountsForSnapshot.push({
      id: `snapshot:${row.snapshot_id}:${row.platform_id}`,
      name,
      alias: platform?.alias || "",
      balance: fromCents(row.balance_cents),
      balanceCurrency: normalizeCurrency(
        row.balance_currency,
        getPlatformCurrency(name)
      ),
      withdrawn: "0",
      withdrawnCurrency: normalizeCurrency(
        row.balance_currency,
        getPlatformCurrency(name)
      ),
      deposited: "0",
      depositedCurrency: normalizeCurrency(
        row.balance_currency,
        getPlatformCurrency(name)
      ),
    });
    acc.set(row.snapshot_id, accountsForSnapshot);

    return acc;
  }, new Map());

  const history = (snapshots || []).map((snapshot) => ({
    date: snapshot.snapshot_date,
    base: fromCents(snapshot.base_bankroll_cents ?? settings?.base_bankroll_cents),
    baseCurrency: INIT_SIMPLIFIED_BANKROLL.baseCurrency,
    adjustedTotal: fromCents(snapshot.total_balance_cents),
    adjustedTotalCurrency: "BRL",
    accounts: snapshotAccountsBySnapshotId.get(snapshot.snapshot_id) || [
      {
        id: `snapshot:${snapshot.snapshot_id}`,
        name: "Snapshot",
        alias: "",
        balance: fromCents(snapshot.total_balance_cents),
        balanceCurrency: "BRL",
        withdrawn: "0",
        withdrawnCurrency: "BRL",
        deposited: "0",
        depositedCurrency: "BRL",
      },
    ],
    platforms: {},
  }));

  return normalizeSimplifiedBankroll({
    ...(INIT_SIMPLIFIED_BANKROLL || {}),
    base: fromCents(settings?.base_bankroll_cents),
    accounts,
    platforms: platformsByName,
    withdrawals,
    deposits,
    history,
  });
};

export const saveSimplifiedBankrollToSupabase = async (userId, bankroll) => {
  const normalized = normalizeSimplifiedBankroll(bankroll);
  const accountEntries = normalized.accounts || [];
  const totalBalanceCents = toCents(
    adjustedPlatformTotal(normalized, { displayCurrency: "BRL" })
  );
  const baseBankrollCents = toCents(normalized.base);

  const { error: settingsError } = await supabase.from("simplified_settings").upsert(
    {
      user_id: userId,
      base_bankroll_cents: toCents(normalized.base),
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id" }
  );
  if (settingsError) throw settingsError;

  let { data: existingPlatforms, error: existingError } = await supabase
    .from("simplified_platforms")
    .select("platform_id,name,alias")
    .eq("user_id", userId);
  if (isMissingAliasError(existingError)) {
    const fallback = await supabase
      .from("simplified_platforms")
      .select("platform_id,name")
      .eq("user_id", userId);
    existingPlatforms = (fallback.data || []).map((platform) => ({
      ...platform,
      alias: "",
    }));
    existingError = fallback.error;
  }
  if (existingError) throw existingError;

  const accountKey = (account) => `${account.name}\u0000${account.alias || ""}`;
  const existingByName = new Map(
    (existingPlatforms || []).map((platform) => [
      accountKey(platform),
      platform,
    ])
  );
  const activeKeys = new Set(accountEntries.map(accountKey));

  const inactiveIds = (existingPlatforms || [])
    .filter((platform) => !activeKeys.has(accountKey(platform)))
    .map((platform) => platform.platform_id);

  if (inactiveIds.length) {
    const { error } = await supabase
      .from("simplified_platforms")
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq("user_id", userId)
      .in("platform_id", inactiveIds);
    if (error) throw error;
  }

  const upsertedPlatforms = [];
  for (const account of accountEntries) {
    const existing = existingByName.get(accountKey(account));
    const currency = getPlatformCurrency(account.name);
    const payload = {
      user_id: userId,
      name: account.name,
      alias: account.alias || "",
      balance_cents: toCents(account.balance),
      balance_currency: normalizeCurrency(account.balanceCurrency, currency),
      withdrawn_cents: toCents(account.withdrawn),
      withdrawn_currency: normalizeCurrency(account.withdrawnCurrency, currency),
      deposited_cents: toCents(account.deposited),
      deposited_currency: normalizeCurrency(account.depositedCurrency, currency),
      is_active: true,
      updated_at: new Date().toISOString(),
    };
    const query = existing
      ? supabase
          .from("simplified_platforms")
          .update(payload)
          .eq("user_id", userId)
          .eq("platform_id", existing.platform_id)
          .select("platform_id,name,balance_cents")
          .single()
      : supabase
          .from("simplified_platforms")
          .insert(payload)
          .select("platform_id,name,balance_cents")
          .single();

    let { data, error } = await query;
    if (
      isMissingCurrencyColumnError(error) ||
      isMissingWithdrawnCentsError(error) ||
      isMissingDepositedCentsError(error) ||
      isMissingAliasError(error)
    ) {
      const {
        balance_currency: _balanceCurrency,
        withdrawn_currency: _withdrawnCurrency,
        deposited_currency: _depositedCurrency,
        ...payloadWithoutCurrency
      } = payload;
      const { alias: _alias, ...payloadWithoutAlias } = payloadWithoutCurrency;
      const { deposited_cents: _depositedCents, ...payloadWithoutDeposit } =
        payloadWithoutCurrency;
      const { withdrawn_cents: _withdrawnCents, ...payloadWithoutLedger } =
        payloadWithoutDeposit;
      const fallbackPayload = isMissingAliasError(error)
        ? payloadWithoutAlias
        : isMissingCurrencyColumnError(error)
          ? payloadWithoutCurrency
        : isMissingDepositedCentsError(error)
          ? payloadWithoutDeposit
          : payloadWithoutLedger;
      const fallbackQuery = existing
        ? supabase
            .from("simplified_platforms")
            .update(fallbackPayload)
            .eq("user_id", userId)
            .eq("platform_id", existing.platform_id)
            .select("platform_id,name,balance_cents")
            .single()
        : supabase
            .from("simplified_platforms")
            .insert(fallbackPayload)
            .select("platform_id,name,balance_cents")
            .single();

      ({ data, error } = await fallbackQuery);

      if (
        error &&
        fallbackPayload !== payloadWithoutLedger &&
        (isMissingWithdrawnCentsError(error) || isMissingDepositedCentsError(error))
      ) {
        const legacyQuery = existing
          ? supabase
              .from("simplified_platforms")
              .update(payloadWithoutLedger)
              .eq("user_id", userId)
              .eq("platform_id", existing.platform_id)
              .select("platform_id,name,balance_cents")
              .single()
          : supabase
              .from("simplified_platforms")
              .insert(payloadWithoutLedger)
              .select("platform_id,name,balance_cents")
              .single();

        ({ data, error } = await legacyQuery);
      }
    }
    if (error) throw error;
    upsertedPlatforms.push(data);
  }

  const today = getLocalDateString();

  if (!accountEntries.length) {
    const { data: todaySnapshots, error: findSnapshotError } = await supabase
      .from("simplified_bankroll_snapshots")
      .select("snapshot_id")
      .eq("user_id", userId)
      .eq("snapshot_date", today);
    if (findSnapshotError) throw findSnapshotError;

    const snapshotIds = (todaySnapshots || []).map((snapshot) => snapshot.snapshot_id);
    if (!snapshotIds.length) return;

    const { error: deleteSnapshotPlatformsError } = await supabase
      .from("simplified_snapshot_platforms")
      .delete()
      .in("snapshot_id", snapshotIds);
    if (deleteSnapshotPlatformsError) throw deleteSnapshotPlatformsError;

    const { error: deleteSnapshotsError } = await supabase
      .from("simplified_bankroll_snapshots")
      .delete()
      .eq("user_id", userId)
      .in("snapshot_id", snapshotIds);
    if (deleteSnapshotsError) throw deleteSnapshotsError;
    return;
  }

  const { data: snapshot, error: snapshotError } = await supabase
    .from("simplified_bankroll_snapshots")
    .upsert(
      {
        user_id: userId,
        snapshot_date: today,
        total_balance_cents: totalBalanceCents,
        base_bankroll_cents: baseBankrollCents,
      },
      { onConflict: "user_id,snapshot_date" }
    )
    .select("snapshot_id")
    .single();

  if (isMissingSnapshotBaseColumnError(snapshotError)) {
    const legacySnapshot = await supabase
      .from("simplified_bankroll_snapshots")
      .upsert(
        {
          user_id: userId,
          snapshot_date: today,
          total_balance_cents: totalBalanceCents,
        },
        { onConflict: "user_id,snapshot_date" }
      )
      .select("snapshot_id")
      .single();

    if (legacySnapshot.error) throw legacySnapshot.error;
    return saveSimplifiedSnapshotPlatforms({
      snapshot: legacySnapshot.data,
      upsertedPlatforms,
    });
  }

  if (snapshotError) throw snapshotError;

  await saveSimplifiedSnapshotPlatforms({
    snapshot,
    upsertedPlatforms,
  });
};

const saveSimplifiedSnapshotPlatforms = async ({ snapshot, upsertedPlatforms }) => {
  const { error: deleteSnapshotPlatformsError } = await supabase
    .from("simplified_snapshot_platforms")
    .delete()
    .eq("snapshot_id", snapshot.snapshot_id);
  if (deleteSnapshotPlatformsError) throw deleteSnapshotPlatformsError;

  const snapshotPlatforms = upsertedPlatforms.map((platform) => ({
    snapshot_id: snapshot.snapshot_id,
    platform_id: platform.platform_id,
    balance_cents: platform.balance_cents,
    balance_currency: getPlatformCurrency(platform.name),
  }));

  if (snapshotPlatforms.length) {
    let { error } = await supabase
      .from("simplified_snapshot_platforms")
      .upsert(snapshotPlatforms, { onConflict: "snapshot_id,platform_id" });
    if (isMissingCurrencyColumnError(error)) {
      const legacySnapshotPlatforms = snapshotPlatforms.map(
        ({ balance_currency: _balanceCurrency, ...snapshotPlatform }) =>
          snapshotPlatform
      );
      ({ error } = await supabase
        .from("simplified_snapshot_platforms")
        .upsert(legacySnapshotPlatforms, { onConflict: "snapshot_id,platform_id" }));
    }
    if (error) throw error;
  }
};
